import { useMutation } from '@tanstack/react-query';

import type { ResponseError } from 'Backend/common/types';
import type { operations } from 'Models/users.api.types';

import Config from 'Config';
import { patchWithoutOverride } from 'Redux/api';
import { catchExpectedErrors } from '../common/catchExpectedErrors';
import { getCurrentUserId } from 'Lib/Utils';

type ApiOperation = operations['updateUser'];
type ResponseStatusCode = keyof ApiOperation['responses'];
type ResponseBody<T extends ResponseStatusCode> = ApiOperation['responses'][T]['content']['application/json'];

export type UpdatableAttributes = ApiOperation['requestBody']['content']['application/json'];

const supportedStatusCodes = [200, 400, 403, 500] as const;

const isSupportedStatusCode = (status: number): status is (typeof supportedStatusCodes)[number] =>
    supportedStatusCodes.some((expected) => status === expected);

export type ResponseSuccess = ResponseBody<200>;

type UpdateParams = operations['updateUser']['requestBody']['content']['application/json'] & {
    userId?: string;
};

const updateUser = async ({
    userId,
    firstName,
    lastName,
    gender,
    country,
    countryCode,
    countryAreaCode,
    mobile,
    referral,
    notificationSettings,
    file,
    fileType,
    idVerified,
}: UpdateParams): Promise<ResponseSuccess> => {
    const url = new URL(`${Config.users_v1_api}/${userId || getCurrentUserId()}`);

    const { data, status } = await catchExpectedErrors(() =>
        patchWithoutOverride(
            url.toString(),
            {
                firstName,
                lastName,
                gender,
                country,
                countryCode,
                countryAreaCode,
                mobile,
                referral,
                notificationSettings,
                file,
                fileType,
                idVerified,
            },
            null,
            true,
            true
        )
    );

    /* eslint-disable no-throw-literal -- Want to throw the response object directly and pass it on to react query */

    if (!isSupportedStatusCode(status)) {
        throw {
            title: 'Unknown error',
            detail: 'Something went wrong',
        } satisfies ResponseError;
    }

    switch (status) {
        case 200:
            return data as ResponseBody<typeof status> satisfies ResponseSuccess;
        case 400:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 403:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 500:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        default:
            // Default is unreachable. Typescript gives an error if it is not.
            throw status satisfies never;
    }

    /* eslint-enable no-throw-literal -- Want to throw the response object directly and pass it on to react query */
};

export const useUpdateUser = () =>
    useMutation<ResponseSuccess, ResponseError, Parameters<typeof updateUser>[0], unknown>({
        mutationFn: updateUser,
    });
